<template>
  <CoreExpand
    :title="payHeading"
    @open="open = $event"
  >
    <p
      v-if="success"
      :class="{ 'opacity-0 pointer-events-none select-none' : !open }"
      class="absolute text-3 form-fade"
    >
      Thanks for your payment, an recept will be emailed to you.
    </p>
    <form
      class="w-full lg:w-1/2 form-fade"
      :class="{ 'opacity-0 pointer-events-none select-none' : success }"
      novalidate="true"
    >
      <h3 class="text-3 mb-3 leading-none">
        Payment Details
      </h3>
      <CoreInput
        label="Email Address"
        :required="true"
        :disabled="processing"
        :value.sync="form.email"
        type="text"
        :error="findError('email').error"
        :error-message="findError('email').message"
        @blur="$event.interacted && isEmail()"
      />
      <CoreInput
        label="Property Address"
        :disabled="processing"
        :value.sync="form.property"
        type="text"
      />
      <div class="flex flex-col lg:flex-row">
        <CoreInput
          label="Invoice Number"
          :disabled="processing"
          :value.sync="form.invoice"
          type="text"
          class="lg:mr-3 lg:w-2/3"
          :error="findError('invoice').error"
          :error-message="findError('invoice').message"
          @blur="$event.interacted && isInvoice()"
        />
        <CoreInput
          label="Amount"
          :required="true"
          :disabled="processing"
          prefix="£"
          :value.sync="form.amount"
          type="text"
          :validator="isNumber"
          :disable-paste="true"
          class="lg:ml-3 lg:w-1/3"
          :error="findError('amount').error"
          :error-message="findError('amount').message"
          @blur="$event.interacted && handleBlurAmount()"
        />
      </div>
      <h3 class="text-3 mb-3 leading-none">
        Billing Details
      </h3>
      <div class="relative">
        <div class="text-4 mb-1 text-black required">
          Credit or Debit Card
        </div>
        <div ref="stripeCardNumber" />
        <CoreError
          :visible="findError('stripeCardNumber').error"
          :message="findError('stripeCardNumber').message"
          :position="{
            transform: 'translateY(-1.25rem)'
          }"
        />
      </div>
      <div class="flex flex-col lg:flex-row">
        <div class="flex-grow lg:mr-3 lg:w-1/3 relative">
          <div class="text-4 mb-1 text-black required">
            Expiry
          </div>
          <div ref="stripeCardExpiry" />
          <CoreError
            :visible="findError('stripeCardExpiry').error"
            :message="findError('stripeCardExpiry').message"
            :position="{
              transform: 'translateY(-1.25rem)'
            }"
          />
        </div>
        <div class="flex-grow lg:mx-3 lg:w-1/3 relative">
          <div class="text-4 mb-1 text-black required">
            CVC
          </div>
          <div ref="stripeCardCvc" />
          <CoreError
            :visible="findError('stripeCardCvc').error"
            :message="findError('stripeCardCvc').message"
            :position="{
              transform: 'translate(-50%, -1.25rem)',
              left: '50%'
            }"
          />
        </div>
        <CoreInput
          class="lg:ml-3 lg:w-1/3"
          label="Post Code"
          :required="true"
          :disabled="processing"
          :value.sync="form.postcode"
          type="text"
          :error="findError('postcode').error"
          :error-message="findError('postcode').message"
          @blur="$event.interacted && isPostcode()"
        />
      </div>
      <div class="relative">
        <CoreAsyncButton
          :suspense="processing"
          class="mb-0"
          @click="submit"
        />
        <CoreError
          :visible="findError('form').error"
          :message="findError('form').message"
          :position="{
            transform: 'translateY(0.25rem)',
          }"
        />
      </div>
    </form>
  </CoreExpand>
</template>

<script>
import loadStripe from '@vendor/stripe';
import { colors, fontSize, fontFamily } from '@utils/tailwind';
import { isEmail, isNumber } from '@utils/fieldValidation';
import { tenantArea } from '@io';
import { ERROR_CODES, INVOICE_PREFIXS } from '@const';

const stripeElements = new Map([
  ['stripeCardNumber', 'cardNumber'],
  ['stripeCardCvc', 'cardCvc'],
  ['stripeCardExpiry', 'cardExpiry'],
]);

const cardElements = {};

export default {
  name: 'TenantAreaPay',

  props: {
    payHeading: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    open: false,

    processing: false,
    stripe: null,
    success: false,

    // Form Elements
    form: {
      email: '',
      property: '',
      invoice: '',
      amount: '',
      postcode: '',
    },

    errors: new Map([]),
    errorsTracker: 0,
  }),

  watch: {
    processing(val) {
      // eslint-disable-next-line no-restricted-syntax
      for (const [element] of stripeElements) {
        this.$refs[element].classList[val ? 'add' : 'remove']('StripeElement--disabled');
      }

      Object.values(cardElements).forEach((element) => element.update({ disabled: val }));
    },
    errorsTracker() {
      // Bit of a brute force hack
      this.$forceUpdate();
    },
  },

  async mounted() {
    this.stripe = await loadStripe();

    const elementsOptions = {
      fonts: [{
        family: fontFamily.body[0],
        // cors blocked on a require asset
        src: 'url("https://firebasestorage.googleapis.com/v0/b/claydon-92e0a.appspot.com/o/staticAssets%2FRMNeue-Regular.woff?alt=media&token=ab3dd974-1cc4-46cb-9299-6332dd11693d") format("woff")',
      }],
    };

    const elements = this.stripe.elements(elementsOptions);

    const options = {
      style: {
        base: {
          color: colors.black,
          backgroundColor: colors.white,
          fontSize: `${parseFloat(fontSize['3']) * 16}px`, // Convert to px
          fontFamily: fontFamily.body.join(', '),
          fontSmoothing: 'antialiased',
          '::selection': {
            backgroundColor: colors.theme1,
          },
          // ':-webkit-autofill': {
          //   '-webkit-box-shadow': `0 0 0px 1000px ${colors.white} inset`,
          // },
          ':disabled': {
            backgroundColor: colors.theme1,
          },
        },
      },
      placeholder: '',
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const [element, type] of stripeElements) {
      const instance = elements.create(type, options);
      // Add element to the dom
      instance.mount(this.$refs[element]);
      // Setup error handling
      instance.on('change', ({ error, empty }) => {
        if (error) {
          this.addError(element, error.message);
          return;
        }
        this.removeError(element);

        if (empty) this.addError(element, ERROR_CODES[1]);
      });


      cardElements[element] = instance;
    }
  },

  methods: {
    async submit() {
      this.processing = true;

      if (!this.validateAllFields()) {
        this.processing = false;
        return;
      }

      const { validated, failed, clientSecret } = (await tenantArea.submit({
        form: this.form,
      })).data;

      if (!validated) {
        failed.forEach((fail) => {
          this.addError(fail.field, ERROR_CODES[fail.code]);
        });

        return;
      }

      const paymentResult = await this.stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElements.stripeCardNumber,
          billing_details: {
            address: {
              postal_code: this.postcode,
            },
          },
        },
      });

      if (paymentResult.error) {
        this.addError('form', paymentResult.error.message);
        this.processing = false;
        return;
      }

      // Sucsess
      this.success = true;
    },

    handleBlurAmount() {
      if (this.isAmount()) {
        this.formatNumber();
      }
    },

    formatNumber() {
      this.$set(
        this.form,
        'amount',
        (Math.round(this.form.amount * 100) / 100).toFixed(2),
      );
    },

    // Form validation methods
    validateAllFields() {
      this.clearErrors();

      // check inline validations
      if (!this.isEmail()) return false;
      if (!this.isInvoice()) return false;

      // Invoice number or property address validation
      if (this.form.property === '' && this.form.invoice === '') {
        this.addError('form', ERROR_CODES[7]);
        return false;
      }

      return true;
    },
    isNumber(value, e) {
      if (e.key.length !== 1) return true;

      if (isNumber(`${value}${e.key}`)) { return false; }

      return true;
    },
    isEmail() {
      this.removeError('email');

      if (this.form.email === '') {
        this.addError('email', ERROR_CODES[1]);
        return false;
      }

      if (!isEmail(this.form.email)) {
        this.addError('email', ERROR_CODES[4]);
        return false;
      }

      return true;
    },
    isInvoice() {
      this.removeError('invoice');

      // This is not a erquired field, handle required validation at the end
      if (this.form.invoice === '') return true;

      const prefix = this.form.invoice.substring(0, 3).toUpperCase();

      if (!INVOICE_PREFIXS.includes(prefix)) {
        this.addError('invoice', ERROR_CODES[5]);
        return false;
      }

      // Removed length validation
      // if (this.form.invoice.length !== 7) {
      //   this.addError('invoice', ERROR_CODES[6]);
      //   return false;
      // }

      return true;
    },
    isAmount() {
      this.removeError('amount');

      if (this.form.amount === '') {
        this.addError('amount', ERROR_CODES[1]);
        return false;
      }

      if (isNumber(this.form.amount)) {
        this.addError('amount', ERROR_CODES[8]);
        return false;
      }

      return true;
    },
    isPostcode() {
      this.removeError('postcode');

      if (this.form.postcode === '') {
        this.addError('postcode', ERROR_CODES[1]);
        return false;
      }

      return true;
    },

    addError(field, error) {
      this.errors.set(field, error);
      this.errorsTracker += 1;
    },
    removeError(field) {
      if (this.errors.has(field)) this.errors.delete(field);
      this.errorsTracker += 1;
    },
    findError(field) {
      return {
        error: this.errors.has(field),
        message: this.errors.get(field) || '',
      };
    },
    clearErrors() {
      this.errors.clear();
    },
  },
};
</script>

<style scoped>
.form-fade {
  transition-property: opacity;
  transition-duration: var(--transition-short);
  transition-timing-function: var(--transition-ease);
}

.required:after {
  content: " *";
}

.mb-0 {
  margin-bottom: 0;
}

.StripeElement {
  @apply rounded-input;
  @apply px-2 py-1 mb-6;
  background-color: theme('colors.white');
  border: 2px solid white;
  transition-property: all;
  transition-duration: var(--transition-long);
  transition-timing-function: var(--transition-ease);
}

.StripeElement--focus {
  outline: none;
  border: 2px solid black;
}

.StripeElement--disabled {
  outline: none;
  background-color: theme('colors.theme1');
  border: 2px solid theme('colors.theme1');
}

.StripeElement--invalid {
    border: 2px solid theme(colors.red);
}
</style>
